import type {
  DoorFinish,
  KitchenMaterial,
  MaterialShading,
  TextureSet,
  ZoneKey,
} from '@cfg/shared';

/** What the compositor needs to apply a material to a zone. */
export interface MaterialBinding {
  /** Stable key for change detection (e.g. the material id). */
  key: string;
  /** Texture URL, or null for a flat colour. */
  textureUrl: string | null;
  shading: MaterialShading;
  /** Multiplies `shading.sizeMm` (zone overrides). */
  scale: number;
  /** Added to `shading.rotationDeg` (zone overrides). */
  extraRotationDeg: number;
  /** Flat albedo (sRGB hex) used when there is no texture. */
  color?: string;
}

/** Picks the smallest texture tier that is at least `preferred` px (or the largest available). */
export function pickTextureTier(textures: TextureSet, preferred: number): string {
  const tiers = Object.keys(textures.tiers)
    .map(Number)
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  const chosen = tiers.find((t) => t >= preferred) ?? tiers[tiers.length - 1];
  return textures.tiers[String(chosen)]!;
}

export function kitchenMaterialBinding(
  material: KitchenMaterial,
  zone: ZoneKey,
  preferredTextureSize: number,
): MaterialBinding {
  const override = material.zoneOverrides[zone];
  return {
    key: `${material.id}@${zone}`,
    textureUrl: pickTextureTier(material.textures, preferredTextureSize),
    shading: material.shading,
    scale: override?.scale ?? 1,
    extraRotationDeg: override?.rotationDeg ?? 0,
    color: material.textures.averageColor,
  };
}

export function doorFinishBinding(
  finish: DoorFinish,
  preferredTextureSize: number,
): MaterialBinding {
  return {
    key: finish.id,
    textureUrl: finish.texture ? pickTextureTier(finish.texture, preferredTextureSize) : null,
    shading: finish.shading,
    scale: 1,
    extraRotationDeg: 0,
    color: finish.color,
  };
}

export function hexToLinear(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  const toLinear = (c: number) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return [toLinear((n >> 16) & 255), toLinear((n >> 8) & 255), toLinear(n & 255)];
}

/** Uniform values for a binding: [sizeW m, sizeH m, cos, sin], [gloss, spec, metallic, hasTex], [tint, enabled]. */
export function bindingUniforms(binding: MaterialBinding): {
  a: [number, number, number, number];
  b: [number, number, number, number];
  c: [number, number, number, number];
} {
  const s = binding.shading;
  const rotation = ((s.rotationDeg + binding.extraRotationDeg) * Math.PI) / 180;
  const tint = hexToLinear(s.tint);
  const flat = binding.textureUrl ? [1, 1, 1] : hexToLinear(binding.color ?? '#bfbfbf');
  return {
    a: [
      (s.sizeMm.w / 1000) * binding.scale,
      (s.sizeMm.h / 1000) * binding.scale,
      Math.cos(rotation),
      Math.sin(rotation),
    ],
    b: [s.gloss, s.specular, s.metallic, binding.textureUrl ? 1 : 0],
    c: [tint[0] * flat[0]!, tint[1] * flat[1]!, tint[2] * flat[2]!, 1],
  };
}
